import { AxiosError } from "axios";
import React, { useState } from "react";
import { client } from "../api/client";
import Navbar from "../components/ui/navbar";
import useAuthStore from "../store/useAuthStore";

const ProfilePage: React.FC = () => {
  const user = useAuthStore((s) => s.user);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{
    current?: string;
    next?: string;
    confirm?: string;
    form?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const validate = () => {
    const errors: typeof fieldErrors = {};
    if (!currentPassword) errors.current = "Current password is required";
    if (!newPassword) errors.next = "New password is required";
    else if (newPassword.length < 8)
      errors.next = "Password must be at least 8 characters";
    if (confirm !== newPassword) errors.confirm = "Passwords do not match";
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage(null);
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await client.post("/auth/me/change-password/", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setSuccessMessage("Password changed successfully.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirm("");
      setFieldErrors({});
    } catch (error) {
      const axiosErr = error as AxiosError<Record<string, unknown>>;
      const data = axiosErr.response?.data ?? {};
      const nextErrors: typeof fieldErrors = {};

      const current = (data as { current_password?: unknown }).current_password;
      if (Array.isArray(current) && typeof current[0] === "string") {
        nextErrors.current = current[0] as string;
      }
      const next = (data as { new_password?: unknown }).new_password;
      if (Array.isArray(next) && typeof next[0] === "string") {
        nextErrors.next = next[0] as string;
      }
      if (!nextErrors.current && !nextErrors.next) {
        const detail = (data as { detail?: string }).detail;
        nextErrors.form = detail ?? "Failed to change password.";
      }
      setFieldErrors(nextErrors);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) return null;

  const joined = new Date(user.date_joined).toLocaleDateString();

  return (
    <>
      <div className="relative flex-shrink-0 w-full py-8 border-b border-1">
        <Navbar />
      </div>
      <div className="flex justify-center">
        <div className="flex-shrink-0 w-full">
          <div className="max-w-2xl p-6 m-6 mx-auto bg-white border border-gray-200 rounded-lg shadow-lg">
            <h2 className="mb-6 text-2xl font-bold text-gray-900">
              My Profile
            </h2>

      <div className="p-5 mb-6 border border-gray-200 rounded-lg bg-gray-50">
        <dl className="grid gap-3 md:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-gray-500">Email</dt>
            <dd className="text-sm text-gray-900">{user.email}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Role</dt>
            <dd className="text-sm text-gray-900 capitalize">{user.role}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Joined</dt>
            <dd className="text-sm text-gray-900">{joined}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Staff</dt>
            <dd className="text-sm text-gray-900">
              {user.is_staff ? "Yes" : "No"}
            </dd>
          </div>
        </dl>
      </div>

      <h3 className="mb-4 text-lg font-semibold text-gray-900">
        Change password
      </h3>

      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        {fieldErrors.form && (
          <div
            className="p-4 text-sm text-red-800 border border-red-200 rounded-lg bg-red-50"
            role="alert"
          >
            {fieldErrors.form}
          </div>
        )}
        {successMessage && (
          <div
            className="p-4 text-sm text-green-800 border border-green-200 rounded-lg bg-green-50"
            role="alert"
          >
            {successMessage}
          </div>
        )}

        <div>
          <label
            htmlFor="current"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            Current password <span className="text-red-500">*</span>
          </label>
          <input
            type="password"
            id="current"
            name="current"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            className={`bg-white border text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ${
              fieldErrors.current ? "border-red-500" : "border-gray-300"
            }`}
          />
          {fieldErrors.current && (
            <p className="mt-1 text-sm text-red-600">{fieldErrors.current}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="next"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            New password <span className="text-red-500">*</span>
          </label>
          <input
            type="password"
            id="next"
            name="next"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            className={`bg-white border text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ${
              fieldErrors.next ? "border-red-500" : "border-gray-300"
            }`}
          />
          {fieldErrors.next && (
            <p className="mt-1 text-sm text-red-600">{fieldErrors.next}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="confirm"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            Confirm new password <span className="text-red-500">*</span>
          </label>
          <input
            type="password"
            id="confirm"
            name="confirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            className={`bg-white border text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ${
              fieldErrors.confirm ? "border-red-500" : "border-gray-300"
            }`}
          />
          {fieldErrors.confirm && (
            <p className="mt-1 text-sm text-red-600">{fieldErrors.confirm}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
        >
          {isSubmitting ? "Saving..." : "Change password"}
        </button>
      </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfilePage;
